import { useMemo } from 'react';

import { Label, DataTable, ErrorMessage, Loading, type TableColumn } from '@/src/ui';
import type { ExamComparisonDTO } from '../models/examComparisonDTOs';
import { ReportCellStyle, ReportHeaderStyle, Constants } from '@/src/utils';

interface ExamComparisonTableProps {
    data: ExamComparisonDTO[];
    isLoading: boolean;
    error: string;
    viewMode: string;
}

export default function ExamComparisonTable({
    data,
    isLoading,
    error,
    viewMode,
}: ExamComparisonTableProps) {
    const { benchmark, others } = useMemo(() => {
        const benchmark: ExamComparisonDTO[] = [];
        const others: ExamComparisonDTO[] = [];
        data.forEach((item) => {
            if (item.examTypeId === Constants.EXAM_TYPE_MASTER_IDS.BENCHMARK) benchmark.push(item);
            else others.push(item);
        });
        return { benchmark, others };
    }, [data]);

    const isComparison = (row: ExamComparisonDTO) =>
        !!row.groupLevel && row.groupLevel !== viewMode;

    const splitRows = (rows: ExamComparisonDTO[]) => ({
        primary: rows.filter((r) => !isComparison(r)),
        pinned: rows.filter(isComparison),
    });

    // Flatten bands and include color info
    const flattenBands = (rows: ExamComparisonDTO[]) =>
        rows.map((row) => ({
            ...row,
            ...Object.fromEntries(
                Object.entries(row.bands).map(([key, value]) => [
                    key,
                    value != null ? `${value}%` : '--',
                ])
            ),
        }));

    const { primary: benchmarkPrimary, pinned: benchmarkPinned } = splitRows(benchmark);
    const { primary: otherPrimary, pinned: otherPinned } = splitRows(others);

    const benchmarkData = flattenBands(benchmarkPrimary);
    const benchmarkPinnedData = flattenBands(benchmarkPinned);
    const otherData = flattenBands(otherPrimary);
    const otherPinnedData = flattenBands(otherPinned);

    const buildColumns = (rows: typeof benchmarkData): TableColumn[] => {
        if (!rows.length) return [];
        const bandKeys = Object.keys(rows[0]).filter(
            (key) =>
                ![
                    'groupName',
                    'gradeShortName',
                    'studentsTested',
                    'averageScore',
                    'examTypeId',
                    'examId',
                    'examName',
                    'groupLevel',
                    'bands',
                ].includes(key)
        );

        const bandColumns: TableColumn[] = bandKeys.map((key) => ({
            dataKey: key,
            header: key,
            allowSort: true,
            align: 'center',
            formatter: (row: any) => {
                const value = row[key];
                if (!value) return '--';
                return <Label color={Constants.bandColors[key]}>{value}</Label>;
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        }));

        return [
            {
                dataKey: 'groupName',
                header: 'Group',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: any) => {
                    const level = row.groupLevel;
                    if (level && level !== viewMode) {
                        const label = level === 'school' ? 'School Avg' : 'District Avg';
                        return (
                            <span
                                style={{
                                    fontWeight: 600,
                                    color: 'var(--pm-dark)',
                                }}
                            >
                                {row.groupName}{' '}
                                <span style={{ fontSize: '0.75em', fontWeight: 400 }}>
                                    ({label})
                                </span>
                            </span>
                        );
                    }
                    return row.groupName || '-';
                },
            },
            {
                dataKey: 'studentsTested',
                header: 'Students Tested',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'averageScore',
                header: 'Average Score',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: any) => {
                    const value = row.averageScore;
                    if (value == null) return '--';
                    return `${value}%`;
                },
            },
            ...bandColumns,
        ];
    };

    if (isLoading) return <Loading message="Loading Exam Comparison Report..." />;
    if (error) return <ErrorMessage error={error} />;

    if (
        !benchmarkData.length &&
        !benchmarkPinnedData.length &&
        !otherData.length &&
        !otherPinnedData.length
    ) {
        return (
            <div className="w-full text-center py-12 text-gray-600">
                No exam comparison data available.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 w-full">
            {(benchmarkData.length > 0 || benchmarkPinnedData.length > 0) && (
                <div className="w-full overflow-auto">
                    <hr className="border-t border-gray-300 my-2" />
                    <br />

                    <div className="pl-4">
                        <h2
                            className="inline-block text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide whitespace-nowrap"
                            style={{
                                fontFamily: 'Verdana, Geneva, sans-serif',
                                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                            }}
                        >
                            Benchmark
                        </h2>
                    </div>
                    <DataTable
                        columns={buildColumns(
                            benchmarkData.length ? benchmarkData : benchmarkPinnedData
                        )}
                        data={benchmarkData}
                        pinnedBottomRows={benchmarkPinnedData}
                        tableName="Exam Comparison - Benchmark"
                        rowsPerPageInitial={50}
                        enableExportCSV
                        enableExportExcel
                        alternatingRowColor
                    />
                </div>
            )}

            {(otherData.length > 0 || otherPinnedData.length > 0) && (
                <div className="w-full overflow-auto">
                    <hr className="border-t border-gray-300 my-2" />
                    <br />

                    <div className="pl-4">
                        <h2
                            className="inline-block text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide whitespace-nowrap"
                            style={{
                                fontFamily: 'Verdana, Geneva, sans-serif',
                                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                            }}
                        >
                            Unit Test & Diagnostic
                        </h2>
                    </div>
                    <DataTable
                        columns={buildColumns(otherData.length ? otherData : otherPinnedData)}
                        data={otherData}
                        pinnedBottomRows={otherPinnedData}
                        tableName="Exam Comparison - Others"
                        rowsPerPageInitial={50}
                        enableExportCSV
                        enableExportExcel
                        alternatingRowColor
                        defaultSortColumn="groupName"
                        defaultSortDirection="asc"
                    />
                </div>
            )}
        </div>
    );
}
